/**
 * Fetches product/variant metafields via the Storefront API GraphQL endpoint.
 *
 * Workaround for a confirmed Shopify platform bug: on non-default storefront
 * locale/market URLs (e.g. /en/, /en-es/), Liquid's `product.metafields...`
 * accessor returns blank for `number_decimal` and `json` type metafields
 * (verified: `number_integer` fields render fine, `number_decimal`/`json`
 * don't — same product, same request, confirmed with real data via the
 * Admin API). The Storefront API is unaffected since it doesn't go through
 * the same Liquid resolution layer.
 *
 * The Storefront API access token is intentionally public/client-side safe —
 * it's scoped to only publicly-visible storefront data (same pattern used by
 * every Shopify headless/Hydrogen storefront).
 */
window.ProductMetafieldsFetcher = (function () {
  const cache = new Map();

  /**
   * @param {string} productHandle
   * @param {Array<{namespace: string, key: string}>} productIdentifiers - product-level metafields to fetch
   * @param {Array<{namespace: string, key: string}>} variantIdentifiers - variant-level metafields to fetch (applied to every variant)
   * @returns {Promise<{product: Object, variantsById: Object<string, Object>}|null>}
   */
  async function fetchProductMetafields(productHandle, productIdentifiers, variantIdentifiers) {
    const cacheKey = productHandle;
    if (cache.has(cacheKey)) return cache.get(cacheKey);

    const config = window.storefrontApiConfig;
    if (!config || !config.url || !config.token) {
      console.error('ProductMetafieldsFetcher: window.storefrontApiConfig is missing'); // eslint-disable-line
      return null;
    }

    const productMetafieldsQuery = productIdentifiers
      .map((id, i) => `pm${i}: metafield(namespace: "${id.namespace}", key: "${id.key}") { value }`)
      .join('\n');

    const variantMetafieldsQuery = variantIdentifiers
      .map((id, i) => `vm${i}: metafield(namespace: "${id.namespace}", key: "${id.key}") { value }`)
      .join('\n');

    const query = `
      query {
        productByHandle(handle: "${productHandle}") {
          ${productMetafieldsQuery}
          variants(first: 100) {
            edges {
              node {
                id
                ${variantMetafieldsQuery}
              }
            }
          }
        }
      }
    `;

    const promise = fetch(config.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.token,
      },
      body: JSON.stringify({ query }),
    })
      .then((response) => response.json())
      .then((json) => {
        if (json.errors || !json.data || !json.data.productByHandle) {
          console.error('ProductMetafieldsFetcher: GraphQL error', json.errors); // eslint-disable-line
          return null;
        }

        const product = json.data.productByHandle;
        const productResult = {};
        productIdentifiers.forEach((id, i) => {
          productResult[`${id.namespace}.${id.key}`] = product[`pm${i}`] ? product[`pm${i}`].value : null;
        });

        const variantsById = {};
        product.variants.edges.forEach(({ node }) => {
          const numericId = node.id.split('/').pop();
          const variantResult = {};
          variantIdentifiers.forEach((id, i) => {
            variantResult[`${id.namespace}.${id.key}`] = node[`vm${i}`] ? node[`vm${i}`].value : null;
          });
          variantsById[numericId] = variantResult;
        });

        return { product: productResult, variantsById };
      })
      .catch((error) => {
        console.error('ProductMetafieldsFetcher: fetch failed', error); // eslint-disable-line
        return null;
      });

    cache.set(cacheKey, promise);
    return promise;
  }

  return { fetchProductMetafields };
})();
